// ToastProvider.jsx — Phase 4 Step A
//
// App-wide transient notifications. `useToast()` exposes notify/dismiss/update;
// the <Toaster> renders a stacked, auto-dismissing list in the corner. Toasts
// are editorial: a kind accent strip (success/error/info/progress), a title, an
// optional message, and a dismiss control. Progress toasts can be promoted to a
// terminal kind via update() — handy for long operations.

import React from 'react';
import { useTheme } from './theme';

const ToastCtx = React.createContext(null);

export function useToast() {
  // Safe no-op fallback so components work even if the provider is absent.
  return React.useContext(ToastCtx) || { notify: () => 0, dismiss: () => {}, update: () => {} };
}

let _seq = 0;

export function ToastProvider({ children }) {
  const [toasts, setToasts] = React.useState([]);
  const timers = React.useRef({});

  const dismiss = React.useCallback((id) => {
    setToasts((list) => list.filter((x) => x.id !== id));
    if (timers.current[id]) { clearTimeout(timers.current[id]); delete timers.current[id]; }
  }, []);

  const schedule = React.useCallback((id, duration) => {
    if (timers.current[id]) { clearTimeout(timers.current[id]); delete timers.current[id]; }
    if (duration && duration > 0) timers.current[id] = setTimeout(() => dismiss(id), duration);
  }, [dismiss]);

  const notify = React.useCallback((opts) => {
    const id = ++_seq;
    // Notifications stay until the user dismisses them (Deepak, 1 Nov: "they
    // should not go away automatically. Let the user clear them"). A caller
    // may still pass a `duration` in ms for one that should time out.
    const toast = { id, kind: 'info', duration: 0, ...(opts || {}) };
    setToasts((list) => [...list, toast]);
    schedule(id, toast.duration);
    return id;
  }, [schedule]);

  const update = React.useCallback((id, patch) => {
    setToasts((list) => list.map((x) => (x.id === id ? { ...x, ...(patch || {}) } : x)));
    if (patch && Object.prototype.hasOwnProperty.call(patch, 'duration')) schedule(id, patch.duration);
  }, [schedule]);

  React.useEffect(() => () => { Object.values(timers.current).forEach(clearTimeout); }, []);

  const clearAll = React.useCallback(() => {
    Object.values(timers.current).forEach(clearTimeout);
    timers.current = {};
    setToasts([]);
  }, []);

  const value = React.useMemo(() => ({ notify, dismiss, update }), [notify, dismiss, update]);

  return (
    <ToastCtx.Provider value={value}>
      {children}
      <Toaster toasts={toasts} onDismiss={dismiss} onClearAll={clearAll} />
    </ToastCtx.Provider>
  );
}

function accentFor(P, kind) {
  if (kind === 'success') return { line: P.okLine, soft: P.okSoft, ink: P.ok };
  if (kind === 'error') return { line: P.dangerLine, soft: P.dangerSoft, ink: P.danger };
  if (kind === 'progress') return { line: P.accentLine, soft: P.accentSoft, ink: P.accent };
  if (kind === 'warn') return { line: P.warnLine, soft: P.warnSoft, ink: P.warn };
  return { line: P.rule, soft: P.elev, ink: P.muted };
}

function Toaster({ toasts, onDismiss, onClearAll }) {
  const { P, FN, MO } = useTheme();
  if (!toasts.length) return null;
  return (
    <div style={{
      position: 'fixed', right: 16, bottom: 16, zIndex: 300,
      display: 'flex', flexDirection: 'column', gap: 10,
      maxWidth: 380, width: 'calc(100vw - 32px)', pointerEvents: 'none',
      maxHeight: 'calc(100vh - 32px)', overflowY: 'auto',
    }}>
      {toasts.length > 1 && (
        <button onClick={onClearAll} style={{
          pointerEvents: 'auto', alignSelf: 'flex-end', fontFamily: MO, fontSize: 10.5,
          padding: '4px 10px', borderRadius: 6, cursor: 'pointer',
          background: P.sheet, color: P.muted, border: `0.5px solid ${P.rule}`,
        }}>Clear all ({toasts.length})</button>
      )}
      {toasts.map((t) => {
        const a = accentFor(P, t.kind);
        const pct = typeof t.fraction === 'number' ? Math.round(Math.max(0, Math.min(1, t.fraction)) * 100) : null;
        return (
          <div key={t.id} style={{
            pointerEvents: 'auto', display: 'flex', alignItems: 'stretch', gap: 0,
            background: P.sheet, border: `0.5px solid ${P.rule}`, borderRadius: 10, overflow: 'hidden',
            boxShadow: '0 14px 36px rgba(20,16,12,.20)', animation: 'mono-in .18s ease both',
          }}>
            <div style={{ width: 3, background: a.ink, flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0, padding: '11px 12px' }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                <span style={{ fontFamily: MO, fontSize: 9, fontWeight: 600, letterSpacing: 0.6, textTransform: 'uppercase', color: a.ink }}>
                  {t.kind === 'success' ? 'Done' : t.kind === 'error' ? 'Problem' : t.kind === 'progress' ? 'Working' : t.kind === 'warn' ? 'Check' : 'Note'}
                </span>
                <span style={{ fontFamily: FN, fontSize: 13, fontWeight: 500, color: P.ink, lineHeight: 1.3 }}>{t.title}</span>
                <button onClick={() => onDismiss(t.id)} title="Dismiss" style={{
                  marginLeft: 'auto', background: 'none', border: 'none', color: P.dim, cursor: 'pointer', fontSize: 15, lineHeight: 1,
                }}>×</button>
              </div>
              {t.message && (
                <div style={{ fontFamily: FN, fontSize: 11.5, color: P.muted, lineHeight: 1.45, marginTop: 3 }}>{t.message}</div>
              )}
              {pct != null && (
                <div style={{ height: 4, borderRadius: 2, background: P.elev, overflow: 'hidden', marginTop: 8 }}>
                  <div style={{ height: '100%', width: `${pct}%`, background: a.ink, transition: 'width .3s ease' }} />
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
